import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from '../../../shared/i18n';
import { autoRewardRulesApi } from '../api/autoRewardRules';
import { familyCurrenciesApi } from '../api/familyCurrencies';
import { monitoredActivitiesApi } from '../api/monitoredActivities';

const RULE_TYPES = ['monitored_activity_approved', 'daily_goal_50', 'daily_goal_100'];
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const emptyForm = () => ({
  rule_type: 'monitored_activity_approved',
  amount: '',
  family_currency_id: '',
  monitored_activity_id: '',
  weekdays: []
});

const ruleToForm = (rule) => ({
  rule_type: rule.rule_type,
  amount: String(rule.amount),
  family_currency_id: String(rule.family_currency?.id ?? ''),
  monitored_activity_id: rule.monitored_activity ? String(rule.monitored_activity.id) : '',
  weekdays: Array.isArray(rule.weekdays) ? [...rule.weekdays] : []
});

const RuleForm = ({
  form,
  setForm,
  currencies,
  monitoredActivities,
  onSubmit,
  onCancel,
  submitting,
  submitLabel,
  t
}) => {
  const isMonitored = form.rule_type === 'monitored_activity_approved';
  const isDailyGoal = form.rule_type === 'daily_goal_50' || form.rule_type === 'daily_goal_100';

  const toggleWeekday = (day) => {
    setForm((prev) => {
      const next = new Set(prev.weekdays);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return { ...prev, weekdays: WEEKDAYS.filter((d) => next.has(d)) };
    });
  };

  return (
    <form
      className="bg-light rounded-2 p-3 mb-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      data-testid="auto-reward-rule-form"
    >
      <div className="mb-3">
        <label className="form-label fw-semibold">{t('auto_reward_rules.form.rule_type')}</label>
        <select
          className="form-select"
          value={form.rule_type}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              rule_type: e.target.value,
              // Reset conditional fields when switching rule type
              monitored_activity_id: '',
              weekdays: []
            }))
          }
          disabled={submitting}
          required
          data-testid="rule-type-select"
        >
          {RULE_TYPES.map((rt) => (
            <option key={rt} value={rt}>
              {t(`auto_reward_rules.rule_type.${rt}`)}
            </option>
          ))}
        </select>
      </div>

      {isMonitored && (
        <div className="mb-3">
          <label className="form-label fw-semibold">
            {t('auto_reward_rules.form.monitored_activity')}
          </label>
          <select
            className="form-select"
            value={form.monitored_activity_id}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, monitored_activity_id: e.target.value }))
            }
            disabled={submitting}
            required
            data-testid="monitored-activity-select"
          >
            <option value="">{t('auto_reward_rules.form.monitored_activity_placeholder')}</option>
            {monitoredActivities.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          {monitoredActivities.length === 0 && (
            <div className="form-text text-warning">
              {t('auto_reward_rules.form.no_monitored_activities')}
            </div>
          )}
        </div>
      )}

      {isDailyGoal && (
        <div className="mb-3">
          <label className="form-label fw-semibold d-block">
            {t('auto_reward_rules.form.weekdays')}
          </label>
          <div className="d-flex flex-wrap gap-2">
            {WEEKDAYS.map((day) => {
              const active = form.weekdays.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => toggleWeekday(day)}
                  disabled={submitting}
                  data-testid={`weekday-toggle-${day}`}
                >
                  {t(`auto_reward_rules.weekdays.${day}`)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">
            {t('auto_reward_rules.form.currency')}
          </label>
          <select
            className="form-select"
            value={form.family_currency_id}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, family_currency_id: e.target.value }))
            }
            disabled={submitting}
            required
            data-testid="currency-select"
          >
            <option value="">{t('auto_reward_rules.form.currency_placeholder')}</option>
            {currencies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label fw-semibold">{t('auto_reward_rules.form.amount')}</label>
          <input
            type="number"
            className="form-control"
            value={form.amount}
            onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
            min="1"
            step="1"
            required
            disabled={submitting}
            data-testid="amount-input"
          />
        </div>
      </div>

      <div className="d-flex gap-2 mt-3">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
          data-testid="submit-rule-btn"
        >
          {submitLabel}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
};

const RuleRow = ({ rule, onEdit, onDelete, busy, t }) => {
  const weekdayLabels = rule.weekdays?.map((d) => t(`auto_reward_rules.weekdays_short.${d}`)) || [];
  return (
    <div className="bg-light rounded-2 p-3 mb-3" data-testid={`rule-${rule.id}`}>
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div className="flex-grow-1">
          <div className="fw-bold mb-1">
            {t(`auto_reward_rules.rule_type.${rule.rule_type}`)}
          </div>
          <div className="text-muted small mb-1">
            {t('auto_reward_rules.row.reward', {
              amount: rule.amount,
              currency: rule.family_currency?.name || ''
            })}
          </div>
          {rule.monitored_activity && (
            <div className="text-muted small">
              {t('auto_reward_rules.row.activity', { name: rule.monitored_activity.name })}
            </div>
          )}
          {weekdayLabels.length > 0 && (
            <div className="text-muted small">
              {t('auto_reward_rules.row.weekdays', { days: weekdayLabels.join(', ') })}
            </div>
          )}
        </div>
        <div className="d-flex gap-2 flex-shrink-0">
          <button
            type="button"
            className="btn btn-sm btn-link text-primary"
            onClick={() => onEdit(rule)}
            disabled={busy}
            aria-label={t('common.edit')}
            data-testid={`edit-rule-${rule.id}`}
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            type="button"
            className="btn btn-sm btn-link text-danger"
            onClick={() => onDelete(rule)}
            disabled={busy}
            aria-label={t('common.delete')}
            data-testid={`delete-rule-${rule.id}`}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export const AutoRewardRulesPage = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const rulesQuery = useQuery({
    queryKey: ['autoRewardRules'],
    queryFn: async () => (await autoRewardRulesApi.list()).data
  });

  const currenciesQuery = useQuery({
    queryKey: ['familyCurrencies'],
    queryFn: async () => (await familyCurrenciesApi.list()).data
  });

  const monitoredActivitiesQuery = useQuery({
    queryKey: ['monitoredActivities'],
    queryFn: async () => (await monitoredActivitiesApi.list()).data
  });

  const rules = rulesQuery.data?.auto_reward_rules || [];
  const currencies = currenciesQuery.data?.family_currencies || [];
  const monitoredActivities = monitoredActivitiesQuery.data?.monitored_activities || [];

  const handleApiError = (err) => {
    setError(err.response?.data?.error?.message || t('common.error'));
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['autoRewardRules'] });

  const createMutation = useMutation({
    mutationFn: (payload) => autoRewardRulesApi.create(payload),
    onSuccess: () => {
      setError(null);
      setSuccessMessage(t('auto_reward_rules.messages.created'));
      setShowForm(false);
      setForm(emptyForm());
      invalidate();
    },
    onError: handleApiError
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => autoRewardRulesApi.update(id, payload),
    onSuccess: () => {
      setError(null);
      setSuccessMessage(t('auto_reward_rules.messages.updated'));
      setEditingId(null);
      setForm(emptyForm());
      invalidate();
    },
    onError: handleApiError
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => autoRewardRulesApi.remove(id),
    onSuccess: () => {
      setError(null);
      setSuccessMessage(t('auto_reward_rules.messages.deleted'));
      invalidate();
    },
    onError: handleApiError
  });

  const busy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const buildPayload = () => {
    const payload = {
      rule_type: form.rule_type,
      amount: parseInt(form.amount, 10),
      family_currency_id: parseInt(form.family_currency_id, 10)
    };
    if (form.rule_type === 'monitored_activity_approved') {
      payload.monitored_activity_id = parseInt(form.monitored_activity_id, 10);
      payload.weekdays = [];
    } else {
      payload.monitored_activity_id = null;
      payload.weekdays = form.weekdays;
    }
    return payload;
  };

  const handleAdd = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (rule) => {
    setForm(ruleToForm(rule));
    setEditingId(rule.id);
    setShowForm(false);
  };

  const handleDelete = (rule) => {
    if (!window.confirm(t('auto_reward_rules.confirm_delete'))) return;
    deleteMutation.mutate(rule.id);
  };

  const handleSubmit = () => {
    const payload = buildPayload();
    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isLoading = rulesQuery.isLoading || currenciesQuery.isLoading || monitoredActivitiesQuery.isLoading;
  const fetchError = rulesQuery.error || currenciesQuery.error || monitoredActivitiesQuery.error;

  const noCurrencies = useMemo(() => currencies.length === 0, [currencies]);

  if (isLoading) {
    return (
      <div className="container py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">
          {t('common.error')}: {fetchError.message}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light">
      <div className="container py-4" data-testid="auto-reward-rules-page">
        <div className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h2 className="h1 fw-bold mb-2">{t('auto_reward_rules.page.title')}</h2>
            <p className="text-muted mb-0">{t('auto_reward_rules.page.description')}</p>
          </div>
          <Link to="/family-currencies" className="btn btn-outline-secondary">
            <i className="bi bi-arrow-left me-1"></i>
            {t('common.back')}
          </Link>
        </div>

        <div className="alert alert-info d-flex align-items-start gap-2" role="alert">
          <i className="bi bi-info-circle-fill flex-shrink-0 mt-1"></i>
          <div>{t('auto_reward_rules.page.owner_notice')}</div>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
            data-testid="auto-reward-rules-error"
          >
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        <div className="row">
          <div className="col-lg-8">
            <div className="card border rounded-3 mb-4">
              <div className="card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <h3 className="h5 fw-bold mb-1">{t('auto_reward_rules.list.title')}</h3>
                    <p className="text-muted small mb-0">
                      {t('auto_reward_rules.list.count', { count: rules.length })}
                    </p>
                  </div>
                  {!showForm && !editingId && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAdd}
                      disabled={busy || noCurrencies}
                      data-testid="add-rule-btn"
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      {t('auto_reward_rules.add_button')}
                    </button>
                  )}
                </div>

                {noCurrencies && (
                  <div className="alert alert-warning">
                    {t('auto_reward_rules.warnings.no_currencies')}{' '}
                    <Link to="/family-currencies">{t('auto_reward_rules.warnings.create_currency_link')}</Link>
                  </div>
                )}

                {rules.length === 0 && !showForm && !editingId && (
                  <p className="text-muted">{t('auto_reward_rules.empty')}</p>
                )}

                {rules.map((rule) => {
                  if (editingId === rule.id) {
                    return (
                      <RuleForm
                        key={rule.id}
                        form={form}
                        setForm={setForm}
                        currencies={currencies}
                        monitoredActivities={monitoredActivities}
                        onSubmit={handleSubmit}
                        onCancel={() => {
                          setEditingId(null);
                          setForm(emptyForm());
                        }}
                        submitting={busy}
                        submitLabel={t('common.save')}
                        t={t}
                      />
                    );
                  }
                  return (
                    <RuleRow
                      key={rule.id}
                      rule={rule}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      busy={busy}
                      t={t}
                    />
                  );
                })}

                {showForm && (
                  <RuleForm
                    form={form}
                    setForm={setForm}
                    currencies={currencies}
                    monitoredActivities={monitoredActivities}
                    onSubmit={handleSubmit}
                    onCancel={() => {
                      setShowForm(false);
                      setForm(emptyForm());
                    }}
                    submitting={busy}
                    submitLabel={t('auto_reward_rules.add_button')}
                    t={t}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoRewardRulesPage;
